// components/EmailEditor/TemplateList.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import ScheduleOptions from "./ScheduleOptions";
import api from "../../services/api";

const TemplateList = ({ onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateShow, setTemplateShow] = useState({ id: null, subject: "", html: "" });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  function handleSuccessMessage(message) {
      toast.success(message, {
        autoClose: 2000,
        position: "top-center",
      });
    }
  
    function handleErrorMessage(message) {
      toast.error(message, {
        autoClose: 2000,
        position: "top-center",
      });
    }

  useEffect(() => {
    fetchTemplates(1, rowsPerPage);
  }, []);

  useEffect(() => {
    fetchTemplates(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const fetchTemplates = async (pageNum = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get("/schedule_email/get_email_templates_reschedule", {
        params: { page: pageNum, page_size: pageSize },
      });
      if (response.data.success) {
        setTemplates(response.data.data);
        setTotalCount(response.data.total_count || 0);
      } else {
        handleErrorMessage("Failed to fetch templates");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      handleErrorMessage(error.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleClick = (template, e) => {
    e.stopPropagation();
    setSelectedTemplate(template);
    setSaveDialogOpen(true);
  };

  const handleSaveOption = async (selectedOption, scheduledTime) => {
    // Handle the save/schedule logic here
    try {
      const response = await api.post("/schedule_email/reschedule_email", {
        temp_id: selectedTemplate.id,
        saveOption: selectedOption,
        scheduledTime: scheduledTime,
      });
      const res = response.data;
      if (res.success) {
        handleSuccessMessage("Email scheduled successfully.");
      }
    } catch (err) {
      console.error("Error scheduling email: ", err);
      handleErrorMessage("Failed to schedule email.");
    } finally {
      setLoading(false);
    }

    setLoading(false);
    setSaveDialogOpen(false);
    setSelectedTemplate(null);
  };

  function formatIsoDateTime(isoString) {
    if (isoString){
      const date = new Date(isoString); // "2025-12-23T07:00:00"

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0"); // 01-12
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");

      return `${dd}-${mm}-${yyyy} ${hh}:${min}` // 23-12-2025 07:00
      }
      return null;
  }

 
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "—";
    const strText = typeof text === "string" ? text : JSON.stringify(text);
    return strText.length > maxLength
      ? strText.substring(0, maxLength) + "..."
      : strText;
  };

  const parseJSON = (data) => {
    try {
      return typeof data === "string" ? JSON.parse(data) : data;
    } catch {
      return data;
    }
  };

  // Filter templates by subjectFor search
  const filteredTemplates = templates.filter((template) => {
    if (!searchTerm.trim()) return true;
    const parsed = parseJSON(template.toCcBccSubject);
    const subject = typeof parsed === "object" ? (parsed.subjectFor || "") : String(parsed || "");
    return subject.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="600" sx={{ color: "#2c3e50" }}>
          Email Templates
        </Typography>

        <Button
          variant="contained"
          onClick={() => navigate("/new_schedule_email")}
          sx={{
            marginRight: "40px",
            textTransform: "none",
            backgroundColor: "#6366f1",
            "&:hover": { backgroundColor: "#4f46e5" },
          }}
        >
          Create
        </Button>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress sx={{ color: "#3498db" }} />
        </Box>
      ) : (
        <>
          {/* Search Bar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              placeholder="Search by subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 320,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#3498db",
                  },
                },
              }}
              size="small"
            />
            <Typography variant="body2" sx={{ color: "#64748b", ml: "auto" }}>
              Showing <strong>{filteredTemplates.length}</strong> of <strong>{templates.length}</strong> templates
            </Typography>
          </Paper>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              borderRadius: 2,
              mb: 3,
            }}
          >
            <div className="overflow-y-auto max-h-[50vh]">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                      width: "80px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    To/Cc/Bcc/Subject
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Scheduled At
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Sent At
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Sent Count
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Attachments
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#3498db",
                      color: "white",
                      fontWeight: 600,
                      width: "120px",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No templates found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template) => {
                    const toCcBcc = parseJSON(template.toCcBccSubject);
                    const attachments = parseJSON(template.attachments);

                    return (
                      <TableRow
                        key={template.id}
                        onClick={() => {
                          const subject = typeof toCcBcc === "object" ? (toCcBcc.subjectFor || "N/A") : "N/A";
                          setTemplateShow({ id: template.id, subject, html: template.html_temp });
                          setShowTemplate(true);
                        }}
                        hover
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#ecf0f1",
                          },
                        }}
                      >
                        <TableCell>{template.id}</TableCell>

                        <Tooltip
                          title={
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {typeof toCcBcc === "object"
                                ? JSON.stringify(toCcBcc, null, 2)
                                : toCcBcc || "N/A"}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <TableCell sx={{ cursor: "pointer" }}>
                            {truncateText(
                              typeof toCcBcc === "object"
                                ? `Subject: ${toCcBcc.subjectFor || "N/A"}`
                                : toCcBcc
                            )}
                          </TableCell>
                        </Tooltip>

                        <TableCell sx={{ cursor: "pointer" }}>
                          {/* {truncateText(template.html_temp, 80)} */}
                          {formatIsoDateTime(template.scheduledAt) || "N/A"}
                        </TableCell>

                        <TableCell sx={{ cursor: "pointer" }}>
                          {formatIsoDateTime(template.sentAt) || "N/A"}
                        </TableCell>

                        <TableCell sx={{ cursor: "pointer" }}>
                          {template.sentCount || "0"}
                        </TableCell>

                        <Tooltip
                          title={
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {Array.isArray(attachments)
                                ? attachments.map((a, i) => (
                                    <div key={i}>
                                      {a.name}: {a.link}
                                    </div>
                                  ))
                                : attachments || "No attachments"}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <TableCell sx={{ cursor: "pointer" }}>
                            {Array.isArray(attachments)
                              ? `${attachments.length} file(s)`
                              : truncateText(attachments)}
                          </TableCell>
                        </Tooltip>

                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => handleScheduleClick(template, e)}
                            sx={{
                              textTransform: "none",
                              backgroundColor: "#27ae60",
                              "&:hover": {
                                backgroundColor: "#229954",
                              },
                            }}
                          >
                            Schedule
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </div>
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: "1px solid #e2e8f0",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  color: "#64748b",
                  fontSize: "0.875rem",
                },
              }}
            />
          </TableContainer>

          {showTemplate && templateShow.id && (
            <Paper
              sx={{
                mt: 2,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  backgroundColor: "#3498db",
                  color: "white",
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  #{templateShow.id} — {templateShow.subject}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowTemplate(false)}
                  sx={{ color: "white" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                dangerouslySetInnerHTML={{ __html: templateShow.html }}
                sx={{
                  p: 2,
                  backgroundColor: "#fff",
                }}
              />
            </Paper>
          )}
        </>
      )}

      <ScheduleOptions
        open={saveDialogOpen}
        onClose={() => {
          setSaveDialogOpen(false);
          setSelectedTemplate(null);
        }}
        onSave={handleSaveOption}
        loading={loading}
        setLoading={setLoading}
      />
    </Box>
  );
};

export default TemplateList;
